import { Injectable } from '@angular/core';
import { initializeApp } from 'firebase/app';
import {
  Auth,
  getAuth,
  onAuthStateChanged,
  sendEmailVerification,
  User,
} from 'firebase/auth';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  firstValueFrom,
  map,
  Observable,
} from 'rxjs';
import { environment } from 'src/environments/environment';

export interface AuthUser extends User {}

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  private readonly _auth: Auth;
  private readonly _userSubject = new BehaviorSubject<AuthUser | null>(null);
  private readonly _authReady = new BehaviorSubject<boolean>(false);
  private readonly authReady$ = this._authReady.asObservable();

  constructor() {
    initializeApp(environment.firebaseConfig);

    this._auth = getAuth();

    onAuthStateChanged(this._auth, (user) => {
      this._userSubject.next(user);
      this._authReady.next(true);
    });
  }

  /**
   * Synchronous getter for current user
   */
  get currentUser(): AuthUser | null {
    return this._userSubject.value;
  }

  /**
   * Observable of current user,
   * Only returns a value when `onAuthStateChanged`
   * has triggered
   */
  get user$(): Observable<AuthUser | null> {
    return combineLatest([
      this.authReady$,
      this._userSubject.asObservable(),
    ]).pipe(
      filter(([ready, _]) => ready),
      map(([_, user]) => user)
    );
  }

  /**
   * Observable of login state,
   * Only returns a value when `onAuthStateChanged`
   * has triggered
   */
  get isLoggedIn$(): Observable<boolean> {
    return combineLatest([
      this.authReady$,
      this._userSubject.asObservable(),
    ]).pipe(
      filter(([ready, _]) => ready),
      map(([_, user]) => user !== null)
    );
  }

  /**
   * Get ID token for current user
   *
   * Note: Uses synchronous user getter
   * @param forceRefresh force a token refresh so server-rewritten custom
   * claims (e.g. MFA state) take effect immediately
   */
  async getToken(forceRefresh = false): Promise<string | null> {
    const user = this.currentUser;
    if (!user) return null;
    return await user.getIdToken(forceRefresh);
  }

  /**
   * Send the Firebase email-verification link to the current user
   */
  async sendVerificationEmail(): Promise<void> {
    const user = this.currentUser;
    if (!user) return;
    await sendEmailVerification(user);
  }

  /**
   * Refresh the user token - useful after initial login to get
   * custom claims.
   * @returns
   */
  async refreshTokenClaims(): Promise<void> {
    const user = await firstValueFrom(this.user$);
    if (!user) return;
    await user.getIdTokenResult(true);
  }

  /**
   * Get Firebase Auth
   * @returns {Auth} auth object
   */
  async signOut(): Promise<void> {
    await this._auth.signOut();
  }
}
